import styled, { keyframes } from "styled-components";

export const PageWrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 1440px;
    margin: 0 auto;
    padding: 40px 24px 80px;
    box-sizing: border-box;

    @media (max-width: 768px) {
        padding: 24px 16px 60px;
    }
`;

export const Breadcrumb = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: 'Repro', sans-serif;
    font-size: 14px;
    color: #6b6560;
    margin-bottom: 20px;
`;

export const BreadcrumbHome = styled.span`
    cursor: pointer;
    color: #6b6560;

    &:hover {
        color: #2e2a28;
    }
`;

export const BreadcrumbSeparator = styled.span`
    color: #b5aea8;
`;

export const BreadcrumbCurrent = styled.span`
    color: #2e2a28;
    font-weight: 500;
`;

export const Greeting = styled.h1`
    font-family: 'Repro', sans-serif;
    font-size: 48px;
    font-weight: 700;
    color: #1a1714;
    margin: 0 0 40px;
    line-height: 1.1;

    @media (max-width: 768px) {
        font-size: 32px;
        margin-bottom: 28px;
    }

    @media (max-width: 480px) {
        font-size: 26px;
    }
`;

export const SearchWrapper = styled.div`
    position: relative;
    width: 100%;
    max-width: 720px;
    margin: 0 auto;
    align-self: center;
`;

export const SearchInputRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    background: #fff;
    border: 1.5px solid #e5e0da;
    border-radius: 50px;
    padding: 14px 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    transition: border-color 0.15s, box-shadow 0.15s;

    &:focus-within {
        border-color: #2e2a28;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    }
`;

export const SearchIcon = styled.span`
    display: flex;
    align-items: center;
    color: #9e9791;
    flex-shrink: 0;
`;

export const SearchInput = styled.input`
    flex: 1;
    border: none;
    outline: none;
    font-family: 'Repro', sans-serif;
    font-size: 16px;
    color: #2e2a28;
    background: transparent;

    &::placeholder {
        color: #b5aea8;
    }

    @media (max-width: 480px) {
        font-size: 14px;
    }
`;

export const InfoButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: 1.5px solid #c8c2bc;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    cursor: pointer;
    color: #6b6560;
    padding: 0;
    transition: border-color 0.15s, color 0.15s;

    &:hover {
        border-color: #2e2a28;
        color: #2e2a28;
    }
`;

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(4px); }
    to   { opacity: 1; transform: translateY(0); }
`;

export const Dropdown = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1), 0 1px 4px rgba(0, 0, 0, 0.06);
    overflow-y: auto;
    max-height: 420px;
    z-index: 100;
    animation: ${fadeIn} 0.15s ease;
`;

export const DropdownItem = styled.div<{ $active?: boolean }>`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 24px;
    cursor: pointer;
    border-bottom: 1px solid #f0ece8;
    transition: background 0.1s;
    background: ${({ $active }) => ($active ? '#f0ece8' : 'transparent')};

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: #faf9f7;
    }

    @media (max-width: 480px) {
        padding: 14px 16px;
        gap: 12px;
    }
`;

export const Avatar = styled.div<{ $bg: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background-color: ${({ $bg }) => $bg};
    flex-shrink: 0;
    font-family: 'Repro', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    letter-spacing: 0.5px;

    @media (max-width: 480px) {
        width: 38px;
        height: 38px;
        font-size: 13px;
    }
`;

export const UserInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

export const UserName = styled.span`
    font-family: 'Repro', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #1a1714;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 480px) {
        font-size: 14px;
    }
`;

export const UserSubtitle = styled.span`
    font-family: 'Repro', sans-serif;
    font-size: 13px;
    color: #9e9791;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const UserTag = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 3px 8px;
    border-radius: 4px;
    background-color: #f5e642;
    font-family: 'Repro', sans-serif;
    font-size: 10px;
    font-weight: 700;
    color: #2e2a28;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
`;

export const UserMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

export const NoResults = styled.div`
    padding: 24px;
    text-align: center;
    font-family: 'Repro', sans-serif;
    font-size: 15px;
    color: #9e9791;
`;

export const LastMessage = styled.span`
    font-family: 'Repro', sans-serif;
    font-size: 12px;
    color: #b5aea8;
    font-style: italic;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 2px;
`;

export const ChannelDate = styled.div`
    margin-left: auto;
    flex-shrink: 0;
    padding-left: 16px;
    text-align: right;
    font-family: 'Repro', sans-serif;
    font-size: 11px;
    color: #9e9791;
    line-height: 1.5;
    white-space: nowrap;
`;

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

export const SpinnerOverlay = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.85);
    z-index: 10;
`;

export const Spinner = styled.span`
    display: inline-block;
    width: 40px;
    height: 40px;
    border: 3px solid rgba(253, 51, 0, 0.25);
    border-top-color: #fd3300;
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;
